import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import IconButton from '@material-ui/core/IconButton';
import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer';
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';

import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import SaveIcon from '@material-ui/icons/Save';
import CancelIcon from '@material-ui/icons/Cancel';

import FileSaver from 'file-saver';

import {
  CustomPaging,
  EditingState,
  FilteringState,
  GroupingState,
  IntegratedGrouping,
  PagingState,
  RowDetailState,
  SortingState,
  SelectionState,
  IntegratedSelection,
} from '@devexpress/dx-react-grid';

import {
  ColumnChooser,
  DragDropProvider,
  Grid as GridBase,
  PagingPanel,
  Table,
  TableColumnReordering,
  TableColumnVisibility,
  TableEditColumn,
  TableEditRow,
  TableFilterRow,
  TableGroupRow,
  TableHeaderRow,
  TableRowDetail,
  Toolbar,
  TableSelection,
} from '@devexpress/dx-react-grid-material-ui';

import Cell from './Cell';

import styles from './styles';

const getCommandComponent = ({ id, onExecute, ...others }) => {
  return {
    add: (<Button
      color="primary"
      onClick={onExecute}
      title="Add"
    >Add</Button>),
    edit: (
      <Tooltip id="tooltip-icon" title="Edit row">
        <IconButton onClick={onExecute}>
          <EditIcon/>
        </IconButton>
      </Tooltip>
    ),
    delete: (
      <Tooltip id="tooltip-icon" title="Delete row">
        <IconButton onClick={onExecute}>
          <DeleteIcon/>
        </IconButton>
      </Tooltip>
    ),
    commit: (
      <Tooltip id="tooltip-icon" title="Save changes">
        <IconButton onClick={onExecute}>
          <SaveIcon/>
        </IconButton>
      </Tooltip>
    ),
    cancel: (
      <Tooltip id="tooltip-icon" title="Cancel changes">
        <IconButton onClick={onExecute}>
          <CancelIcon/>
        </IconButton>
      </Tooltip>
    ),
  }[id];
};

const calculateColumnWidths = (columns, availWidth) => {
  const totalWidth = columns.reduce((res, col) => (col.width || 100) + res, 0);
  const colWidths = columns.map((col) => ({
    columnName: col.name, width: (((col.width || 100) * 1.0) / totalWidth) * (availWidth - 120),
  }));

  return colWidths;
};

// const getColumnWidths = (columnWidths, availWidth) => {
//   const totalWidth = columnWidths.reduce((res, col) => col.width + res, 0);
//   const factor = totalWidth / (availWidth - 120);
//   return columnWidths.map((col) => ({ columnName: col.columnName, width: col.width / factor }));
// };

const getRowComponent = (props) => <Table.Row {...props}/>;

const PAGE_SIZES = [10, 25, 50];

const getFilterCellComponent = (props) => {
  switch (props.column.name) {
    default:
      return <Cell/>;
  }
};

const getDefaultNoCellComponent = (colSpan) => {
  return (<Table.Cell colSpan={colSpan}>
    <div style={{ textAlign: 'center' }}>
      No Data
    </div>
  </Table.Cell>);
};

class Grid extends React.Component {

  state = {
    data: [],
    editingRows: [],
    addedRows: [],
    changedRows: {},
    sorting: [],
    columnWidths: {},
    hiddenColumns: [],
    filters: [],
    selection: [],

    pageSize: 10,
    currentPage: 0,
  };

  componentWillMount() {
    const { data, sorting, columns } = this.props;
    const { pageSize, currentPage } = this.state;
    this.fetchData(this.props.pageSize || pageSize, currentPage);
    this.setState({
      pageSize: this.props.pageSize || pageSize,
      data: data ? data.data : null,
      sorting: sorting || [],
      columnWidths: calculateColumnWidths(columns, 1100),
      columnOrder: columns.map((col) => col.name),
      hiddenColumnNames: columns.filter((col) => col.visible === false).map((col) => col.name),
    });
  }

  commitAdded() {
    if (this.editingState && this.editingState.props.addedRows.length > 0) {
      this.editingState.commitAddedRows({ rowIds: _.range(this.editingState.props.addedRows.length) });
    }
  }

  fetchData = (pageSize = this.state.pageSize, currentPage = this.state.currentPage, filters = this.state.filters, sorting = this.state.sorting) => {
    this.props.fetch(_.assign({
        // pageSize,
        offset: pageSize * currentPage,
        limit: pageSize,
      },
      (filters || []).reduce((res, filter) => _.assign({}, res, { [filter.columnName]: filter.value }), {}),
      sorting && sorting.length > 0 ? { orderBy: `${sorting[0].columnName}-${sorting[0].direction}` } : {},
    ));
  };

  handleEdit = (editingRows) => {
    const row = this.props.data.data[editingRows[editingRows.length - 1]];
    // console.log('handleEdit', row);
    this.props.onEdit(row);
  };

  changeAddedRows = (addedRows) => {
    this.setState({ addedRows });
  };

  handleCommit = ({ added, deleted, changed, ...other }) => {
    console.log('handleCommit', added, deleted, changed, other);

    if (added && added.length > 0) {
      const row = added[0];
      // console.log('handleCommit', row);
      // this.props.onChange(this.props.costs.concat([row]));
      // const newData = {
      //   data: this.props.data.data.concat([row]),
      //   pagination: this.props.data.pagination,
      // };
      this.props.onAdd(row);
    }
    if (changed && _.keys(changed).length > 0) {
      const { data: { data } } = this.props;
      _.keys(changed).forEach((rowId) => {
        data[rowId] = _.assign(data[rowId], changed[rowId]);
      });
      this.props.onChange(data);
    }
    if (deleted && deleted.length > 0) {
      const row = this.props.data.data[deleted[0]];
      console.log('handleRemove', row);

      if (row) {
        this.props.onRemove(row)
          .then(() => this.fetchData())
          .catch((err) => console.log('onRemove', err));
      }
    }
  };

  changeChangedRows = (changedRows) => {
    this.setState({ changedRows });
  };

  changeSorting = (sorting) => {
    // console.log('sorting', sorting);
    this.setState({ sorting }, () => {
      this.fetchData();
    });
  };

  changeCurrentPage = (currentPage) => {
    console.log('changeCurrentPage', currentPage);
    this.setState({
      currentPage,
    }, () => {
      this.fetchData();
    });
  };

  changePageSize = (pageSize) => {
    this.setState({
      pageSize, filters: [],
      currentPage: 0,
    }, () => {
      this.fetchData();
    });
  };

  // commitChanges = ({ added, changed, deleted }) => {
  //   // console.log('commitChanges', added, changed, deleted);
  //   let { data: { data } } = this.props;
  //   if (added) {
  //     added.forEach((cost) => {
  //       // this.props.updateCost(_.assign({ id: uuid() }, cost));
  //     });
  //     // const startingAddedId = (data.length - 1) > 0 ? data[data.length - 1].id + 1 : 0;
  //     // data = [
  //     //   ...data,
  //     //   ...added.map((row, index) => ({
  //     //     id: startingAddedId + index,
  //     //     ...row,
  //     //   })),
  //     // ];
  //   }
  //   if (changed) {
  //     _.keys(changed).forEach((rowId) => {
  //       // this.props.updateCost(_.assign({}, data[rowId], changed[rowId]));
  //     });
  //     // data = data.map(row => (changed[row.id] ? { ...row, ...changed[row.id] } : row));
  //   }
  //   if (deleted) {
  //     const deletedSet = new Set(deleted);
  //     data = data.filter(row => !deletedSet.has(row.id));
  //   }
  //   this.setState({ data });
  // };

  handleSelectionChange = (selection) => {
    console.log('selection', selection);
    this.setState({ selection });
  };

  // handleColumnWidthChange = (col) => {
  //   this.setState({
  //     columnWidths: this.state.columnWidths.map((c) => {
  //       if (col.columnName === c.columnName) {
  //         return col;
  //       }
  //
  //       return c;
  //     })
  //   });
  // };

  handleHiddenColumnNamesChange = (hiddenColumnNames) => {
    // console.log('hiddenColumnNames', hiddenColumnNames);
    this.setState({
      hiddenColumnNames,
    });
  };

  handleChangeColumnOrder = (newOrder) => {
    this.setState({ columnOrder: newOrder });
  };

  handleChangeFilters = (filters) => {
    this.setState({ filters });
    this.props.fetch(filters.reduce((res, filter) => _.assign(res, { [filter.columnName]: filter.value }), {}));
  };

  handleDownload = () => {
    const { filters, sorting } = this.state;
    this.props.download(_.assign(
      (filters || []).reduce((res, filter) => _.assign({}, res, { [filter.columnName]: filter.value }), {}),
      sorting && sorting.length > 0 ? { orderBy: `${sorting[0].columnName}-${sorting[0].direction}` } : {},
    )).then((response) => {
      const blob = new Blob([response.response], { type: 'text/csv;charset=utf-8' });
      FileSaver.saveAs(blob, 'download.csv');
    });
  };

  handleMultipleDelete = () => {
    const { selection } = this.state;
    const { data: { data }, onRemove } = this.props;
    const rows = selection.map((idx) => data[idx]);
    return onRemove(rows)
      .then(() => {
        this.setState({
          selection: [],
        });

        setTimeout(() => this.fetchData(), 1000); // TODO hack
      });
  };

  isColumnTogglingEnabled = (column) => {
    return true;
  };

  renderGrid() {
    const {
      columns, rowComponent, cellComponent, grouping, groupingExtensions,
      pageSizes, columnExtensions, rowDetailComponent, toolbar, columnChooser, data,
      onAdd, onEdit, onRemove, paging, filterCellComponent, inlineCellComponent,
      allowSorting, allowColumnReordering, noDataCellComponent, buttons, download, allowSelection,
    } = this.props;

    // console.log('data', data);

    const {
      editingRows, changedRows, addedRows, selection,
      hiddenColumnNames, columnOrder, pageSize, currentPage,
    } = this.state;

    // if (!data || data.loading) {
    //   return (<Loading/>);
    // }

    return (<div>
      {buttons}
      <GridBase
        rows={data ? data.data || [] : []}
        columns={columns}
      >
        {paging !== false ? <PagingState
          currentPage={currentPage}
          onCurrentPageChange={this.changeCurrentPage}
          pageSize={pageSize}
          onPageSizeChange={this.changePageSize}
        /> : null}
        <SortingState
          sorting={this.state.sorting}
          onSortingChange={this.changeSorting}/>
        {/*<IntegratedSorting/>*/}
        <EditingState
          on
          editingRows={editingRows}
          onEditingRowIdsChange={this.handleEdit}
          changedRows={changedRows}
          onChangedRowsChange={this.changeChangedRows}
          addedRows={addedRows}
          onAddedRowsChange={this.changeAddedRows}
          onCommitChanges={this.handleCommit}
          ref={(editingState) => this.editingState = editingState}
        />
        {filterCellComponent ? <FilteringState
          filters={this.state.filters}
          onFiltersChange={this.handleChangeFilters}/> : null}
        <GroupingState
          grouping={grouping}
        />
        <IntegratedGrouping columnExtensions={groupingExtensions || []}/>
        {/*{filtering ? <IntegratedFiltering*/}
        {/*columnExtensions={filtering}*/}
        {/*/> : null}*/}
        {/*<IntegratedPaging/>*/}
        {paging !== false ? <CustomPaging
          totalCount={data && data.pagination && data.pagination.totalCount ? data.pagination.totalCount : 0}/> : null}
        <DragDropProvider/>
        {rowDetailComponent ? <RowDetailState/> : null}
        {allowSelection ? <SelectionState
          selection={selection}
          onSelectionChange={this.handleSelectionChange}
        /> : null}
        {allowSelection ? <IntegratedSelection/> : null}
        <Table
          rowComponent={rowComponent || getRowComponent}
          cellComponent={(props) => (<Cell>{cellComponent(props)}</Cell>)}
          columnExtensions={columnExtensions}
          noDataRowComponent={noDataCellComponent ? (props) =>
            <Table.Row>{noDataCellComponent(props)}</Table.Row> : () =>
            <Table.Row>{getDefaultNoCellComponent(columns.length + 1)}</Table.Row>}
          noDataCellComponent={noDataCellComponent || getDefaultNoCellComponent}
        />
        {/*<TableColumnResizing*/}
        {/*columnWidths={getColumnWidths(columnWidths, width)}*/}
        {/*onColumnWidthsChange={(col) => this.handleColumnWidthChange(col)}/>*/}
        {allowColumnReordering ? <TableColumnReordering
          order={columnOrder}
          onOrderChange={(order) => this.handleChangeColumnOrder(order)}
        /> : null}
        <TableHeaderRow
          allowSorting={allowSorting}
          showSortingControls={allowSorting}
        />
        {allowSelection ? <TableSelection showSelectAll/> : null}
        {inlineCellComponent ? <TableEditRow cellComponent={inlineCellComponent}/> : null}
        <TableGroupRow/>
        <TableColumnVisibility
          hiddenColumnNames={hiddenColumnNames}
          onHiddenColumnNamesChange={this.handleHiddenColumnNamesChange}
          isColumnTogglingEnabled={this.isColumnTogglingEnabled}
        />
        {toolbar ? <Toolbar/> : null}
        {columnChooser ? <ColumnChooser/> : null}
        {filterCellComponent ?
          <TableFilterRow cellComponent={filterCellComponent || getFilterCellComponent}/> : null}
        {onAdd || onEdit || onRemove ? (<TableEditColumn
          width={120}
          showAddCommand={onAdd !== undefined}
          showEditCommand={onEdit !== undefined}
          showDeleteCommand={onRemove !== undefined}
          commandComponent={getCommandComponent}
        />) : null}
        {rowDetailComponent ? <TableRowDetail contentComponent={rowDetailComponent}/> : null}
        {paging !== false ? <PagingPanel pageSizes={pageSizes || PAGE_SIZES}/> : null}
      </GridBase>

      {download ? <Button style={styles.download} onClick={this.handleDownload}>Download</Button> : null}
      {onRemove && allowSelection && selection.length > 0 ? <Button style={styles.download}
                                                                    onClick={this.handleMultipleDelete}>{`Delete Selected (${selection.length})`}</Button> : null}
    </div>);
  }

  render() {
    if (this.props.autoSizer === false) {
      return this.renderGrid();
    }

    return (<div>
      <AutoSizer disableHeight defaultWidth={300}>
        {({ width }) => {
          return (<div style={{ width }}>
            {this.renderGrid()}
          </div>);
        }}
      </AutoSizer>
    </div>);
  }
};

Grid.propTypes = {
  columns: PropTypes.array.isRequired,
  rowComponent: PropTypes.func,
  cellComponent: PropTypes.func.isRequired,
  columnExtensions: PropTypes.array,
  rowDetailComponent: PropTypes.func,
  paging: PropTypes.bool,
  noDataCellComponent: PropTypes.func,

  data: PropTypes.object,
  sorting: PropTypes.array,
  filtering: PropTypes.array,
  filterCellComponent: PropTypes.func,
  pageSizes: PropTypes.array,
  pageSize: PropTypes.number,
  toolbar: PropTypes.bool,
  toolbarChildren: PropTypes.any,
  columnChooser: PropTypes.bool,
  grouping: PropTypes.array,
  allowSelection: PropTypes.bool,

  fetch: PropTypes.func.isRequired,
  download: PropTypes.func,
  onAdd: PropTypes.func,
  onEdit: PropTypes.func,
  onRemove: PropTypes.func,

  inlineCellComponent: PropTypes.func,
  onChange: PropTypes.func,

  autoSizer: PropTypes.bool,
};

// function mapStateToProps(state) {
//   return {};
// }
//
// function mapDispatchToProps(dispatch) {
//   return bindActionCreators({}, dispatch);
// }

export default Grid; //connect(mapStateToProps, mapDispatchToProps)(Grid);



// WEBPACK FOOTER //
// ./src/components/Common/Grid/index.js